import { DateRangePicker, Input, Select } from "@components";
import { Button } from "@components/button/button-untitledui";
import {
	FILTER_CONTROL_HEIGHT_CLASS,
	FilterBar,
	FilterField,
} from "@components/filter-bar";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { Download01, Plus, SearchLg } from "@untitledui/icons";
import { debounce } from "lodash";
import type { Key } from "react";
import {
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import type { DateValue } from "react-aria";
import { cx } from "@/utils/cx";
import {
	INFRACTION_LIST_STATUS_FILTER_OPTIONS,
	INFRACTION_STATUS_LABEL,
} from "../constants/infraction-status.constant";
import { INFRACTION_LIST_SEARCH_MAX_LENGTH } from "../constants/infraction-table-columns.constant";
import type {
	InfractionListFilters,
	InfractionStatus,
} from "../types/infraction.types";
import { sanitizeInfractionListSearch } from "../utils/infraction-list-search";

type InfractionsFiltersToolbarProps = {
	filters: InfractionListFilters;
	onFiltersChange: (filters: InfractionListFilters) => void;
	onPageChange: (page: number) => void;
	onNewInfraction: () => void;
	onExport: () => void;
};

const STATUS_OPTIONS = [
	{ id: "all", label: "Todos" },
	...INFRACTION_LIST_STATUS_FILTER_OPTIONS.map((value) => ({
		id: value,
		label: INFRACTION_STATUS_LABEL[value],
	})),
];

function calendarDateToIso(date: CalendarDate): string {
	return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

function isoToCalendarDate(iso: string | null): CalendarDate | null {
	if (!iso) {
		return null;
	}
	const [y, m, d] = iso.split("-").map(Number);
	if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
		return null;
	}
	return new CalendarDate(y, m, d);
}

export function InfractionsFiltersToolbar({
	filters,
	onFiltersChange,
	onPageChange,
	onNewInfraction,
	onExport,
}: InfractionsFiltersToolbarProps) {
	const maxDate = today(getLocalTimeZone());
	const searchInputId = useId();
	const [search, setSearch] = useState(filters.search ?? "");

	const lastAppliedSearchRef = useRef(filters.search ?? "");
	const latestProps = useRef({ filters, onFiltersChange, onPageChange });

	useLayoutEffect(() => {
		latestProps.current = { filters, onFiltersChange, onPageChange };
	}, [filters, onFiltersChange, onPageChange]);

	const update = useCallback((patch: Partial<InfractionListFilters>) => {
		const current = latestProps.current;
		current.onFiltersChange({ ...current.filters, ...patch });
		current.onPageChange(1);
	}, []);

	const applySearch = useCallback(
		(term: string) => {
			if (term === (latestProps.current.filters.search ?? "")) {
				return;
			}
			lastAppliedSearchRef.current = term;
			update({ search: term });
		},
		[update]
	);

	const handleDebounce = useMemo(
		() => debounce((value: string) => applySearch(value.trim()), 1000),
		[applySearch]
	);

	useEffect(() => () => handleDebounce.cancel(), [handleDebounce]);

	useEffect(() => {
		const next = filters.search ?? "";
		if (next === lastAppliedSearchRef.current) {
			return;
		}
		lastAppliedSearchRef.current = next;
		handleDebounce.cancel();
		setSearch(next);
	}, [filters.search, handleDebounce]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const sanitizedValue = sanitizeInfractionListSearch(event.target.value);

		setSearch(sanitizedValue);
		if (!sanitizedValue.trim()) {
			handleDebounce.cancel();
			applySearch("");
			return;
		}
		handleDebounce(sanitizedValue);
	};

	const rangeValue = useMemo(() => {
		const start = isoToCalendarDate(filters.deadlineStartDate);
		const end = isoToCalendarDate(filters.deadlineEndDate);
		if (!(start && end)) {
			return null;
		}
		return { start, end };
	}, [filters.deadlineStartDate, filters.deadlineEndDate]);

	const statusLabel = useMemo(() => {
		if (filters.status === "all") {
			return "Status";
		}
		return `Status: ${INFRACTION_STATUS_LABEL[filters.status]}`;
	}, [filters.status]);

	return (
		<FilterBar
			actions={
				<>
					<Button
						aria-label="Exportar infrações"
						color="secondary"
						iconLeading={Download01}
						onClick={onExport}
						size="sm"
					>
						Exportar
					</Button>
					<Button
						aria-label="Nova infração"
						color="primary"
						iconLeading={Plus}
						onClick={onNewInfraction}
						size="sm"
					>
						Nova Infração
					</Button>
				</>
			}
			data-slot="infractions-filters-toolbar"
			label="Filtros de infrações"
		>
			<FilterField
				className="sm:max-w-[600px]"
				grow
				htmlFor={searchInputId}
				label="Buscar infrações"
				labelVisibility="hidden"
			>
				<div className="relative w-full">
					<SearchLg
						aria-hidden
						className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-fg-quaternary"
					/>
					<Input
						aria-label="Buscar por placa, condutor ou número da infração"
						className={cx(FILTER_CONTROL_HEIGHT_CLASS, "pl-9")}
						id={searchInputId}
						maxLength={INFRACTION_LIST_SEARCH_MAX_LENGTH}
						onChange={handleSearchChange}
						placeholder="Buscar por placa, condutor ou número da infração"
						value={search}
					/>
				</div>
			</FilterField>

			<FilterField
				className="sm:max-w-[246px]"
				label="Status"
				labelVisibility="hidden"
			>
				<Select
					aria-label="Filtrar por status"
					className="w-full"
					items={STATUS_OPTIONS}
					onChange={(key: Key | null) => {
						if (key) {
							update({ status: String(key) as InfractionStatus | "all" });
						}
					}}
					placeholder={statusLabel}
					size="sm"
					value={filters.status}
				>
					{(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
				</Select>
			</FilterField>

			<FilterField
				className="sm:min-w-0"
				label="Vencimento"
				labelVisibility="hidden"
			>
				<DateRangePicker
					aria-label="Filtrar por vencimento"
					buttonClassName="w-full"
					className="w-full"
					maxValue={maxDate}
					onChange={(range) => {
						const hasRange =
							range?.start !== undefined && range?.end !== undefined;
						if (!hasRange) {
							update({ deadlineStartDate: null, deadlineEndDate: null });
							return;
						}
						update({
							deadlineStartDate: calendarDateToIso(range.start as CalendarDate),
							deadlineEndDate: calendarDateToIso(range.end as CalendarDate),
						});
					}}
					size="sm"
					value={rangeValue as { start: DateValue; end: DateValue } | null}
				/>
			</FilterField>
		</FilterBar>
	);
}
